"use client";

import { useEffect, useRef } from "react";
import { IsovMediaPlaceholder } from "@/components/stencil";
import ErrorBoundary from "@/components/ErrorBoundary";
import classNames from "@/utils/classNames";
import isovTheme from "@/utils/theme";
import { trackInteraction } from "@/utils/trackInteraction";
import { getCookieValue } from "@/utils/getCookieValue";
import { getMetaData } from "@/utils/getMetaData";

interface TrackedVideoProps {
  theme?: string;
  videoId?: string;
  videoProvider?: "youtube" | "vimeo" | "wistia";
  playlistId?: string;
  startAt?: string | number | null;
  title?: string;
  slot?: string;
  decorated?: boolean;
  shaded?: boolean;
  rounded?: boolean;
  className?: string;
}

declare global {
  interface Window {
    YT: any;
    Vimeo: any;
    Wistia: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

// The YouTube iframe API calls this global once its script has loaded.
function setYouTubeApiReadyHandler(handler: () => void) {
  window.onYouTubeIframeAPIReady = handler;
}

function TrackedVideoImpl({
  theme = "default",
  videoId,
  videoProvider = "youtube",
  playlistId,
  startAt,
  title,
  slot = "",
  decorated,
  shaded,
  rounded = true,
  className,
}: TrackedVideoProps) {
  const componentTheme = isovTheme(theme);
  const playerRef = useRef<any>(null);
  const progressRef = useRef<number[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);

  const loadYouTubeAPI = () => {
    if (window.YT && window.YT.Player) {
      initYouTubePlayer();
    } else {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
      setYouTubeApiReadyHandler(initYouTubePlayer);
    }
  };

  const loadVimeoAPI = () => {
    if (window.Vimeo) {
      initVimeoPlayer();
    } else {
      const tag = document.createElement("script");
      tag.src = "https://player.vimeo.com/api/player.js";
      document.body.appendChild(tag);
      tag.onload = initVimeoPlayer;
    }
  };

  const loadWistiaAPI = () => {
    if (window.Wistia) {
      initWistiaPlayer();
    } else {
      const script = document.createElement("script");
      script.src = `https://fast.wistia.com/assets/external/E-v1.js`;
      script.async = true;
      script.onload = initWistiaPlayer;
      document.body.appendChild(script);
    }
  };

  const parseTimecodeToSeconds = (input?: string | number | null): number => {
    if (input == null || input === "") return 0;

    if (typeof input === "number" && Number.isFinite(input)) {
      return Math.max(0, Math.floor(input));
    }

    const raw = String(input).trim().toLowerCase();
    if (!raw) return 0;

    if (/^\d+$/.test(raw)) {
      return Math.max(0, parseInt(raw, 10));
    }

    const hms = raw.match(/^(\d+):([0-5]?\d):([0-5]?\d)$/);
    if (hms) {
      const [, h, m, s] = hms;
      return Number(h) * 3600 + Number(m) * 60 + Number(s);
    }

    const ms = raw.match(/^(\d+):([0-5]?\d)$/);
    if (ms) {
      const [, m, s] = ms;
      return Number(m) * 60 + Number(s);
    }

    const compact = raw.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
    if (compact) {
      const [, h = "0", m = "0", s = "0"] = compact;
      if (h || m || s) {
        return Number(h || 0) * 3600 + Number(m || 0) * 60 + Number(s || 0);
      }
    }

    return 0;
  };

  const cleanVideoId = videoId ? encodeURIComponent(videoId?.trim()) : null;
  const cleanPlaylistId = playlistId
    ? encodeURIComponent(playlistId?.trim())
    : null;
  const startSeconds = parseTimecodeToSeconds(startAt);
  const iframeId = `video-${videoProvider}-${cleanVideoId}`;

  const trackProgress = (current: number, duration: number) => {
    const percent = Math.floor((current / duration) * 100);
    const rounded = Math.floor(percent / 10) * 10;

    if (
      rounded >= 10 &&
      rounded <= 100 &&
      !progressRef.current.includes(rounded)
    ) {
      progressRef.current.push(rounded);
      trackInteraction(
        {
          event_label: "video_progress",
          video_id: cleanVideoId,
          video_provider: videoProvider,
          video_title: title,
          percent_played: rounded,
          current_time_seconds: Math.round(current),
          duration_seconds: Math.round(duration),
          page_path: window.location.pathname,
          page_url: window.location.href,
          timestamp: new Date().toISOString(),
          identity: getCookieValue("hubspotutk"),
          content_tags: getMetaData("content-tag"),
        },
        "video_progress",
      );
    }
  };

  const initYouTubePlayer = () => {
    playerRef.current = new window.YT.Player(iframeId, {
      cleanVideoId,
      events: {
        onStateChange: (event: any) => {
          const state = event.data;
          const player = playerRef.current;

          if (state === window.YT.PlayerState.PLAYING) {
            trackInteraction(
              {
                event_label: "video_play",
                video_id: cleanVideoId,
                video_provider: "youtube",
                video_title: title,
                page_path: window.location.pathname,
                page_url: window.location.href,
                timestamp: new Date().toISOString(),
                identity: getCookieValue("hubspotutk"),
                content_tags: getMetaData("content-tag"),
              },
              "video_play",
            );

            intervalRef.current = setInterval(() => {
              trackProgress(player.getCurrentTime(), player.getDuration());
            }, 1000);
          }

          if (state === window.YT.PlayerState.PAUSED) {
            trackInteraction(
              {
                event_label: "video_pause",
                video_id: cleanVideoId,
                video_provider: "youtube",
                video_title: title,
                paused_at_seconds: Math.round(player.getCurrentTime()),
                page_path: window.location.pathname,
                page_url: window.location.href,
                timestamp: new Date().toISOString(),
                identity: getCookieValue("hubspotutk"),
                content_tags: getMetaData("content-tag"),
              },
              "video_pause",
            );
          }

          if (state === window.YT.PlayerState.ENDED) {
            trackInteraction(
              {
                event_label: "video_complete",
                video_id: cleanVideoId,
                video_provider: "youtube",
                video_title: title,
                page_path: window.location.pathname,
                page_url: window.location.href,
                timestamp: new Date().toISOString(),
                identity: getCookieValue("hubspotutk"),
                content_tags: getMetaData("content-tag"),
              },
              "video_complete",
            );
            clearInterval(intervalRef.current!);
          }
        },
      },
    });
  };

  const initVimeoPlayer = () => {
    const iframe = document.getElementById(iframeId);
    if (!iframe) return;
    playerRef.current = new window.Vimeo.Player(iframe);

    playerRef.current.on("play", () => {
      trackInteraction(
        {
          event_label: "video_play",
          video_id: cleanVideoId,
          video_provider: "vimeo",
          video_title: title,
          page_path: window.location.pathname,
          page_url: window.location.href,
          timestamp: new Date().toISOString(),
          identity: getCookieValue("hubspotutk"),
          content_tags: getMetaData("content-tag"),
        },
        "video_play",
      );
    });

    playerRef.current.on("pause", (data: any) => {
      trackInteraction(
        {
          event_label: "video_pause",
          video_id: cleanVideoId,
          video_provider: "vimeo",
          video_title: title,
          paused_at_seconds: Math.round(data.seconds),
          page_path: window.location.pathname,
          page_url: window.location.href,
          timestamp: new Date().toISOString(),
          identity: getCookieValue("hubspotutk"),
          content_tags: getMetaData("content-tag"),
        },
        "video_pause",
      );
    });

    playerRef.current.on("ended", () => {
      trackInteraction(
        {
          event_label: "video_complete",
          video_id: cleanVideoId,
          video_provider: "vimeo",
          video_title: title,
          page_path: window.location.pathname,
          page_url: window.location.href,
          timestamp: new Date().toISOString(),
          identity: getCookieValue("hubspotutk"),
          content_tags: getMetaData("content-tag"),
        },
        "video_complete",
      );
    });

    playerRef.current.on("timeupdate", (data: any) => {
      trackProgress(data.seconds, data.duration);
    });
  };

  const initWistiaPlayer = () => {
    const wistiaVideo = window.Wistia.api(cleanVideoId);

    wistiaVideo.bind("play", () => {
      trackInteraction(
        {
          event_label: "video_play",
          video_id: cleanVideoId,
          video_provider: "wistia",
          video_title: title,
          page_path: window.location.pathname,
          page_url: window.location.href,
          timestamp: new Date().toISOString(),
          identity: getCookieValue("hubspotutk"),
          content_tags: getMetaData("content-tag"),
        },
        "video_play",
      );
    });

    wistiaVideo.bind("pause", () => {
      wistiaVideo.time((t: number) => {
        trackInteraction(
          {
            event_label: "video_pause",
            video_id: cleanVideoId,
            video_provider: "wistia",
            video_title: title,
            paused_at_seconds: Math.round(t),
            page_path: window.location.pathname,
            page_url: window.location.href,
            timestamp: new Date().toISOString(),
            identity: getCookieValue("hubspotutk"),
            content_tags: getMetaData("content-tag"),
          },
          "video_pause",
        );
      });
    });

    wistiaVideo.bind("end", () => {
      trackInteraction(
        {
          event_label: "video_complete",
          video_id: cleanVideoId,
          video_provider: "wistia",
          video_title: title,
          page_path: window.location.pathname,
          page_url: window.location.href,
          timestamp: new Date().toISOString(),
          identity: getCookieValue("hubspotutk"),
          content_tags: getMetaData("content-tag"),
        },
        "video_complete",
      );
    });

    wistiaVideo.bind("secondchange", (t: number) => {
      wistiaVideo.duration((d: number) => {
        trackProgress(t, d);
      });
    });
  };

  const getEmbedSrc = (): string => {
    let youTubeParams = "enablejsapi=1&controls=1&rel=0&modestbranding=1";

    if (videoProvider === "vimeo") {
      return `https://player.vimeo.com/video/${cleanVideoId}?autoplay=0&muted=0&title=0&byline=0&portrait=0`;
    }
    if (videoProvider === "wistia") {
      return `https://fast.wistia.net/embed/iframe/${cleanVideoId}?seo=false&videoFoam=true`;
    }
    if (cleanPlaylistId) {
      youTubeParams = `${youTubeParams}&list=${cleanPlaylistId}`;
    }
    if (startSeconds && startSeconds > 0) {
      youTubeParams = `${youTubeParams}&start=${startSeconds}`;
    }

    return `https://www.youtube.com/embed/${cleanVideoId}?${youTubeParams}&mute=0&showinfo=0`;
  };

  useEffect(() => {
    if (videoProvider === "youtube") {
      loadYouTubeAPI();
    } else if (videoProvider === "vimeo") {
      loadVimeoAPI();
    } else if (videoProvider === "wistia") {
      loadWistiaAPI();
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoProvider]);

  return (
    <div
      ref={hostRef}
      slot={slot}
      className={classNames(
        "video relative",
        rounded && "rounded-medium overflow-hidden",
        decorated &&
          `decorated outline-1 outline-offset-8 outline-${componentTheme.outline} outline-dashed`,
        shaded && `shaded theme-${theme}`,
        (decorated || shaded) &&
          `bg-${componentTheme.background} border border-${componentTheme.border}`,
        className,
      )}
    >
      <IsovMediaPlaceholder theme={theme} type="video" />
      <iframe
        id={iframeId}
        width="100%"
        height="100%"
        title={title}
        className="absolute top-0 left-0 bottom-0 right-0 m-auto w-full h-full"
        src={getEmbedSrc()}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
      />
    </div>
  );
}

export default function TrackedVideo(props: TrackedVideoProps) {
  return (
    <ErrorBoundary
      fallback={<IsovMediaPlaceholder theme={props.theme} type="video" />}
    >
      <TrackedVideoImpl {...props} />
    </ErrorBoundary>
  );
}
